import axios from "axios";
import Kichiri from "./KichiriService";
import UserService from "./UserService";
import ConfigurationService from "./ConfigurationService";

const BASE_URL = ConfigurationService.resolveApi().rest;

function appendList(form, name, list) {
	if (Array.isArray(list)) {
		list.forEach(v => v && form.append(name, v));
	} else if (typeof list === "string" && list) {
		list
			.split(",")
			.map(s => s.trim())
			.filter(Boolean)
			.forEach(v => form.append(name, v));
	}
}

const GmailService = {
	// ---------------------------------------------------------------------
	// OAuth / connection (team account)
	// ---------------------------------------------------------------------

	/**
	 * Build a Google OAuth consent URL for the given team.
	 *
	 * @param {Number} teamId
	 * @returns {Promise<string | null>}
	 */
	async getAuthUrl(teamId) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.gmail.authUrl({}, { teamId }, authToken);
			return (response.data && response.data.url) || null;
		} catch (error) {
			console.log(error);
			return null;
		}
	},

	/**
	 * List Gmail accounts the current user can access.
	 */
	async listAccounts() {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.gmail.accountList({}, {}, authToken);
			return (response.data && response.data.accounts) || [];
		} catch (error) {
			console.log(error);
			return [];
		}
	},

	/**
	 * Disconnect a team Gmail account.
	 *
	 * @param {Number} accountId
	 */
	async disconnect(accountId) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.gmail.accountDisconnect({ accountId }, {}, authToken);
			return response.data;
		} catch (error) {
			console.log(error);
			return null;
		}
	},

	// ---------------------------------------------------------------------
	// OAuth / connection (current user)
	// ---------------------------------------------------------------------

	/**
	 * Fetches the OAuth consent URL the current user should be redirected (or popped up) to.
	 *
	 * @returns {Promise<{ url: string } | null>}
	 */
	async getUserAuthUrl() {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.gmail.gmailAuthUrl({}, {}, user.authToken);
			return response.data;
		} catch (error) {
			console.log(error);
			return null;
		}
	},

	/**
	 * Returns whether the current user has Gmail connected and which address.
	 *
	 * @returns {Promise<{ connected: boolean, gmailAddress: string | null, lastUsedAt: string | null }>}
	 */
	async getStatus() {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.gmail.gmailStatus({}, {}, user.authToken);
			return response.data;
		} catch (error) {
			console.log(error);
			return { connected: false, gmailAddress: null, lastUsedAt: null };
		}
	},

	/**
	 * Disconnects the current user's Gmail account.
	 *
	 * @returns {Promise<{ disconnected: boolean } | null>}
	 */
	async disconnectUser() {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.gmail.gmailDisconnect({}, {}, user.authToken);
			return response.data;
		} catch (error) {
			console.log(error);
			return null;
		}
	},

	/**
	 * Re-pushes the current user's open backoffice tasks to their Google Tasks.
	 *
	 * @returns {Promise<{ ok: boolean, attempted?: number, synced?: number, error?: string }>}
	 */
	async resyncTasks() {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.gmail.gmailTasksResync({}, {}, user.authToken);
			if (!response || !response.data) return { ok: false, error: "Request failed" };
			if (response.data.error) return { ok: false, error: response.data.error };
			return Object.assign({ ok: true }, response.data);
		} catch (error) {
			console.log(error);
			return { ok: false, error: (error && error.message) || "Request failed" };
		}
	},

	/**
	 * Opens the Google consent screen in a centered popup and resolves with
	 * the result message posted back by the callback page.
	 *
	 * @param {Object} params
	 * @param {string} params.url
	 * @returns {Promise<{ ok: boolean, gmailAddress: string | null, message: string | null }>}
	 */
	openConsentPopup({ url }) {
		return new Promise(function (resolve) {
			let width = 520;
			let height = 640;
			let left = window.screenX + (window.outerWidth - width) / 2;
			let top = window.screenY + (window.outerHeight - height) / 2;

			let popup = window.open(url, "gmail-oauth", `width=${width},height=${height},left=${left},top=${top}`);

			let resolved = false;

			function handleMessage(event) {
				let payload = event.data;
				if (!payload || payload.source !== "gmail-oauth") return;

				resolved = true;
				window.removeEventListener("message", handleMessage);
				resolve({ ok: !!payload.ok, gmailAddress: payload.gmailAddress || null, message: payload.message || null });
			}

			window.addEventListener("message", handleMessage);

			let pollClosed = setInterval(function () {
				if (popup && popup.closed) {
					clearInterval(pollClosed);
					if (!resolved) {
						window.removeEventListener("message", handleMessage);
						resolve({ ok: false, gmailAddress: null, message: "Popup closed before completing." });
					}
				}
			}, 500);
		});
	},

	// ---------------------------------------------------------------------
	// Threads / messages / labels
	// ---------------------------------------------------------------------

	/**
	 * List threads in a folder.
	 * @param {Object} params
	 * @param {Number} params.accountId
	 * @param {String} params.folder         inbox|sent|drafts|starred|important|trash|spam|all|<labelId>
	 * @param {String} [params.q]            Gmail search syntax (optional)
	 * @param {String} [params.pageToken]
	 * @param {Number} [params.maxResults]
	 * @returns {Promise<{threads: Array, nextPageToken: String|null, resultSizeEstimate: Number}>}
	 */
	async listThreads({ accountId, folder = "inbox", q = "", pageToken, maxResults = 25 }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.gmail.threadList({}, { accountId, folder, q, pageToken, maxResults }, authToken);
			return response.data || { threads: [], nextPageToken: null };
		} catch (error) {
			console.log(error);
			return { threads: [], nextPageToken: null, error: error.response && error.response.data && error.response.data.error };
		}
	},

	/**
	 * Full thread with messages and bodies.
	 */
	async getThread({ accountId, threadId }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.gmail.threadGet({}, { accountId, threadId }, authToken);
			return response.data || null;
		} catch (error) {
			console.log(error);
			return null;
		}
	},

	/**
	 * Apply a flag action (star/unstar/markRead/markUnread/markImportant/markUnimportant) or raw label add/remove.
	 */
	async updateMessage({ accountId, messageId, action, addLabels, removeLabels }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.gmail.messageUpdate(
				{ accountId, messageId, action, addLabels, removeLabels },
				{},
				authToken,
			);
			return response.data;
		} catch (error) {
			console.log(error);
			return null;
		}
	},

	async trashMessage({ accountId, messageId, threadId }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.gmail.messageTrash({ accountId, messageId, threadId }, {}, authToken);
			return response.data;
		} catch (error) {
			console.log(error);
			return null;
		}
	},

	async listLabels(accountId) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.gmail.labelList({}, { accountId }, authToken);
			return (response.data && response.data.labels) || [];
		} catch (error) {
			console.log(error);
			return [];
		}
	},

	// ---------------------------------------------------------------------
	// Sending / drafts
	// ---------------------------------------------------------------------

	/**
	 * Send a message (new / reply / forward). Multipart so attachments can ride along.
	 * @returns {Promise<{id, threadId, labelIds}|null>}
	 */
	async sendMessage({ accountId, to, cc, bcc, subject, html, threadId, inReplyTo, references, mode, files }) {
		let { authToken } = UserService.getUserData();
		const form = new FormData();
		form.append("accountId", String(accountId));
		appendList(form, "to", to);
		appendList(form, "cc", cc);
		appendList(form, "bcc", bcc);
		if (subject) form.append("subject", subject);
		if (html) form.append("html", html);
		if (threadId) form.append("threadId", threadId);
		if (inReplyTo) form.append("inReplyTo", inReplyTo);
		if (references) form.append("references", references);
		if (mode) form.append("mode", mode);
		(files || []).forEach(f => form.append("files", f));
		try {
			const response = await axios.post(`${BASE_URL}/gmail/message-send`, form, {
				headers: { Authorization: authToken, "Content-Type": "multipart/form-data" },
			});
			return response.data;
		} catch (error) {
			console.log(error);
			return null;
		}
	},

	async createDraft({ accountId, to, cc, bcc, subject, html, threadId, aiPrompt, aiModel }) {
		let { authToken } = UserService.getUserData();
		try {
			const response = await Kichiri.gmail.draftCreate(
				{ accountId, to, cc, bcc, subject, html, threadId, aiPrompt, aiModel },
				{},
				authToken,
			);
			return response.data;
		} catch (error) {
			console.log(error);
			return null;
		}
	},

	async updateDraft({ accountId, draftId, to, cc, bcc, subject, html, threadId, aiPrompt, aiModel }) {
		let { authToken } = UserService.getUserData();
		try {
			const response = await Kichiri.gmail.draftUpdate(
				{ accountId, draftId, to, cc, bcc, subject, html, threadId, aiPrompt, aiModel },
				{},
				authToken,
			);
			return response.data;
		} catch (error) {
			console.log(error);
			return null;
		}
	},

	async listDrafts(accountId) {
		let { authToken } = UserService.getUserData();
		try {
			const response = await Kichiri.gmail.draftList({}, { accountId }, authToken);
			return (response.data && response.data.drafts) || [];
		} catch (error) {
			console.log(error);
			return [];
		}
	},

	async getDraft({ accountId, draftId }) {
		let { authToken } = UserService.getUserData();
		try {
			const response = await Kichiri.gmail.draftGet({}, { accountId, draftId }, authToken);
			return response.data;
		} catch (error) {
			console.log(error);
			return null;
		}
	},

	async deleteDraft({ accountId, draftId }) {
		let { authToken } = UserService.getUserData();
		try {
			const response = await Kichiri.gmail.draftDelete({ accountId, draftId }, {}, authToken);
			return response.data;
		} catch (error) {
			console.log(error);
			return null;
		}
	},

	async sendDraft({ accountId, draftId, to, cc, bcc, subject, html, threadId, inReplyTo, references, mode, files }) {
		let { authToken } = UserService.getUserData();
		const form = new FormData();
		form.append("accountId", String(accountId));
		form.append("draftId", String(draftId));
		appendList(form, "to", to);
		appendList(form, "cc", cc);
		appendList(form, "bcc", bcc);
		if (subject !== undefined) form.append("subject", subject);
		if (html !== undefined) form.append("html", html);
		if (threadId) form.append("threadId", threadId);
		if (inReplyTo) form.append("inReplyTo", inReplyTo);
		if (references) form.append("references", references);
		if (mode) form.append("mode", mode);
		(files || []).forEach(f => form.append("files", f));
		try {
			const response = await axios.post(`${BASE_URL}/gmail/draft-send`, form, {
				headers: { Authorization: authToken, "Content-Type": "multipart/form-data" },
			});
			return response.data;
		} catch (error) {
			console.log(error);
			return null;
		}
	},

	// ---------------------------------------------------------------------
	// Attachments
	// ---------------------------------------------------------------------

	/**
	 * Build the URL to download an attachment. Includes auth via query so <a download> works directly.
	 */
	attachmentUrl({ accountId, messageId, attachmentId, disposition = "attachment" }) {
		let { authToken } = UserService.getUserData();
		const params = new URLSearchParams({
			accountId: String(accountId),
			messageId,
			attachmentId,
			disposition,
			authToken,
		});
		return `${BASE_URL}/gmail/attachment-get?${params.toString()}`;
	},
};

export default GmailService;